import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { getSpotsAction } from 'actions/actions';
import { trackEvent } from 'utils/analytics';
import { AppState } from 'reducers/reducers';
import UploadIcon from 'assets/icons/upload.svg?react';
import { DocumentUploadCategory, SUBCATEGORY_LABELS } from 'types/Documents';
import { useDocuments } from 'contexts/DocumentsContext';
import PropertyDocuments from 'components/PropertyDocuments/PropertyDocuments';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import { ModalRef } from 'components/Modal/Modal';
import Button from 'components/Button/Button';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import useFilters from 'hooks/useFilters';
import Loader from 'components/Loader/Loader';

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const DocumentsModuleContainer = ({ orgSpots, orgSpotsLoading, push: connectedPush, getSpots }: Props) => {
    const { documents, categories, documentCategories, loading, errorMessage, getDocuments, uploadDocuments } =
        useDocuments();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { w9: _w9, ...uploadableCategories } = documentCategories;
    const uploadCategories = Object.values(uploadableCategories).filter((c) => c !== undefined);

    const uploadModalRef = useRef<ModalRef>(null);

    const { filters, urlUpdater } = useFilters<{ spotPk: string }>({
        filterValidators: {
            spotPk: (currentValue: string | null): string | null => {
                if (!orgSpots) return null; // still loading — don't write to URL yet
                const spots = Object.keys(orgSpots);
                if (currentValue && orgSpots[currentValue]) return currentValue; // multi-spot: keep valid selection
                return spots[0]; // auto-select first spot
            },
        },
        push: connectedPush,
    });

    const currentSpot = useMemo(
        () => (orgSpots && filters.spotPk ? orgSpots[filters.spotPk] : null),
        [orgSpots, filters.spotPk]
    );

    const spotSelectOptions = useMemo(() => {
        const spotsList = orgSpots ? Object.values(orgSpots) : [];
        return spotsList.map((spot) => ({ value: String(spot.pk), label: spot.name }));
    }, [orgSpots]);

    useEffect(() => {
        if (!orgSpots) getSpots();
    }, [orgSpots, getSpots]);

    useEffect(() => {
        if (currentSpot?.uuid) {
            getDocuments(currentSpot.uuid);
            trackEvent('Documents Page Viewed', { spotName: currentSpot.name, spot: currentSpot.pk });
        }
    }, [currentSpot?.uuid, getDocuments]);

    if (orgSpotsLoading) return <Loader />;

    if (!orgSpots) return <div>No spots found for this organization</div>;

    const handleUpload = async (files: File[], category: DocumentUploadCategory) => {
        if (!currentSpot) return;
        await uploadDocuments(currentSpot.uuid, files, category);
        trackEvent('Documents Page Uploaded', {
            spotName: currentSpot.name,
            spot: currentSpot.pk,
            type: category.sub_category ? SUBCATEGORY_LABELS[category.sub_category] : category.category,
        });
    };
    const showSpotSelect = spotSelectOptions.length > 1;

    return (
        <DashboardContent
            title="Documents"
            actionItem={
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: showSpotSelect ? 'auto 100px' : '100px',
                        gap: '12px',
                    }}
                >
                    {showSpotSelect && (
                        <AGSimpleSelect
                            selected={filters.spotPk}
                            onSelect={urlUpdater.spotPk}
                            options={spotSelectOptions}
                            placeholder="Select a property"
                        />
                    )}
                    <Button
                        color="primary"
                        size="sm"
                        disabled={!currentSpot}
                        onClick={() => uploadModalRef.current?.openModal()}
                    >
                        <UploadIcon /> Upload
                    </Button>
                </div>
            }
        >
            <PropertyDocuments
                documents={documents ?? []}
                categories={categories}
                uploadCategories={uploadCategories}
                isLoading={loading}
                errorMessage={errorMessage}
                onUpload={handleUpload}
                uploadModalRef={uploadModalRef}
            />
        </DashboardContent>
    );
};

function mapStateToProps(state: AppState) {
    return {
        orgSpots: state.spots.orgSpots,
        orgSpotsLoading: state.spots.orgSpotsLoading,
    };
}

const mapDispatchToProps = {
    push,
    getSpots: getSpotsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentsModuleContainer);
